/**
 * telemetry — a single, dependency-free funnel for errors & events.
 *
 * Why this exists: the app had no crash/error reporting, so production
 * failures were invisible. Rather than hard-wire a vendor (Sentry/PostHog),
 * this provides a stable internal API the whole app can call today, with a
 * pluggable sink so a vendor can be attached later WITHOUT touching call sites.
 *
 * Wiring a provider later is a one-liner at app start:
 *   import { setTelemetrySink } from '@/shared/services/telemetry';
 *   setTelemetrySink({
 *     reportError: (e, ctx) => Sentry.captureException(e, { extra: ctx }),
 *     reportEvent: (n, p) => posthog.capture(n, p),
 *   });
 *
 * Until a sink is set, errors are logged (dev) and swallowed quietly (prod),
 * so calling these is always safe and never throws.
 */

import { isChunkLoadError } from '@/shared/utils/chunkReload';

export interface TelemetrySink {
    reportError(error: unknown, context?: Record<string, unknown>): void;
    reportEvent(name: string, props?: Record<string, unknown>): void;
    /** Tie subsequent events to a known user (funnel analytics). Optional. */
    identify?(distinctId: string, traits?: Record<string, unknown>): void;
    /** Forget the current user (call on sign-out). Optional. */
    reset?(): void;
}

let sink: TelemetrySink | null = null;

/** Attach a telemetry provider (call once at app start). */
export function setTelemetrySink(s: TelemetrySink): void {
    sink = s;
}

function toError(error: unknown): { message: string; stack?: string } {
    if (error instanceof Error) return { message: error.message, stack: error.stack };
    return { message: typeof error === 'string' ? error : JSON.stringify(error) };
}

/** Report a caught error. Never throws. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
    try {
        if (sink) {
            sink.reportError(error, context);
            return;
        }
        // Also expose a global hook so a vendor init script can capture without
        // importing this module (e.g. a Sentry snippet in index.html).
        const hook = (globalThis as { __KARR_REPORT_ERROR__?: (e: unknown, c?: unknown) => void }).__KARR_REPORT_ERROR__;
        if (hook) {
            hook(error, context);
            return;
        }
        if (import.meta.env.DEV) {
            const { message, stack } = toError(error);
            console.error('[telemetry] error:', message, context ?? '', stack ?? '');
        }
    } catch {
        /* telemetry must never break the app */
    }
}

/** Report a product/usage event. Never throws. */
export function reportEvent(name: string, props?: Record<string, unknown>): void {
    try {
        if (sink) {
            sink.reportEvent(name, props);
            return;
        }
        if (import.meta.env.DEV) {
            console.debug('[telemetry] event:', name, props ?? '');
        }
    } catch {
        /* no-op */
    }
}

/** Tie subsequent events to a signed-in user. Never throws. */
export function identify(distinctId: string, traits?: Record<string, unknown>): void {
    try {
        sink?.identify?.(distinctId, traits);
    } catch {
        /* no-op */
    }
}

/** Forget the current user (call on sign-out). Never throws. */
export function resetIdentity(): void {
    try {
        sink?.reset?.();
    } catch {
        /* no-op */
    }
}


/**
 * Failures of the VISITOR'S environment, not of Karr.
 *
 * The crash log is only worth reading if what is in it is actionable. On 19 Aug
 * 2026 the first digest came back **20 of 27 signatures** being one thing:
 * `registerSW.js` rejecting on marketing pages, always an anonymous visitor.
 * Service-worker registration legitimately rejects in a private window, on
 * Brave and Firefox-private, behind some extensions, and for crawlers. There is
 * nothing to fix and no user harmed — the app runs fine without a service
 * worker, it just loses offline caching.
 *
 * Reported anyway, they buried the one real page crash in the same fortnight.
 * Same reasoning `RouteError` already applies to stale chunks: a thing that is
 * expected, harmless and unfixable is not a defect, and logging it costs the
 * defects that are.
 *
 * ⚠ Keep this list SHORT and specific. Every entry is a class of failure nobody
 * will ever be told about again, so it must be one that is genuinely someone
 * else's environment. When in doubt, let it through — a noisy log is a nuisance,
 * a silent one is a lie.
 */
const ENVIRONMENTAL_NOISE = [
    // vite-plugin-pwa's injected registerSW.js; its promise has no catch.
    /registerSW\.js/i,
    /ServiceWorkerContainer\.register/i,
    /Failed to register a ServiceWorker/i,
    /The operation is insecure/i,
];

function isEnvironmentalNoise(err: unknown): boolean {
    if (!err) return false;
    // A stale chunk after a deploy: chunkReload.ts heals it, and RouteError
    // already keeps it out of the log. Same answer when it escapes React.
    if (isChunkLoadError(err)) return true;
    const e = err as { message?: unknown; stack?: unknown };
    const parts: string[] = [];
    if (typeof err === 'string') parts.push(err);
    if (typeof e.message === 'string') parts.push(e.message);
    if (typeof e.stack === 'string') parts.push(e.stack);
    const text = parts.join(' ');
    if (!text.trim()) return false;
    return ENVIRONMENTAL_NOISE.some((re) => re.test(text));
}

/**
 * Install global handlers for uncaught errors and unhandled promise rejections.
 * Call once at app startup. These are the failures React error boundaries can't
 * catch (async, event handlers, non-render code).
 */
export function installGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('error', (e) => {
        if (isEnvironmentalNoise(e.error ?? e.message)) return;
        reportError(e.error ?? e.message, { kind: 'window.onerror' });
    });
    window.addEventListener('unhandledrejection', (e) => {
        if (isEnvironmentalNoise(e.reason)) return;
        reportError(e.reason, { kind: 'unhandledrejection' });
    });
}
